import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import ProtectedRoute from "@/components/auth/ProtectedRoute.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { readLastAuthedPath } from "@/lib/authedPathCache";
import { readLastRole, writeLastRole } from "@/lib/roleCache";
import {
  hasLocalPharmacyAccess,
  hasPersistedPharmacyOwnerProfile,
  mergePersistedPharmacyMembers,
  readPharmacyProfile,
  resolvePersistedPharmacyAccess,
  savePharmacyProfile,
} from "@/services/pharmacyLaneService.js";
import supabase from "@/services/supabaseClient.js";

const PHARMACY_ONBOARDING_PATH = "/onboarding/pharmacy";
const PHARMACY_ROLE_HINTS = new Set([
  "pharmacy",
  "pharmacist",
  "pharmd",
  "pharmacy_owner",
  "pharmacy_staff",
]);

function canBypassPharmacyAccessOnLocalhost() {
  try {
    const host = String(window.location.hostname || "").trim().toLowerCase();
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host === "::1" ||
      host.endsWith(".local");
    return !!import.meta.env.DEV && isLocal;
  } catch {
    return false;
  }
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRoleHint(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isStagingLikeRuntime() {
  try {
    const host = String(window.location.hostname || "").trim().toLowerCase();
    return (
      import.meta.env.DEV === true ||
      host === "staging.afrimed.tech" ||
      host.endsWith("--afrimed-staging.netlify.app")
    );
  } catch {
    return false;
  }
}

async function isPharmacyInvitee(email = "") {
  const cleanEmail = normalizeEmail(email);
  if (!cleanEmail || !isStagingLikeRuntime()) return false;

  const { data, error } = await supabase
    .from("staging_access_invites")
    .select("allowed,role_hint")
    .eq("email", cleanEmail)
    .maybeSingle();

  if (error) return false;
  if (data?.allowed !== true) return false;
  return PHARMACY_ROLE_HINTS.has(normalizeRoleHint(data?.role_hint));
}

async function hasPharmacyOnboardingStarted(userId = "") {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId || !isStagingLikeRuntime()) return false;

  const { data, error } = await supabase
    .from("onboarding_events")
    .select("event,step")
    .eq("user_id", cleanUserId)
    .in("event", ["pharmacy_role_selected", "pharmacy_onboarding_started"])
    .limit(1);

  if (error) return false;
  return Array.isArray(data) && data.length > 0;
}

function PharmacyGate({ children }) {
  const auth = useAuth() || {};
  const location = useLocation();
  const allowLocalFallback = canBypassPharmacyAccessOnLocalhost();
  const [state, setState] = React.useState({
    loading: true,
    allowed: false,
    redirectToOnboarding: false,
    actorId: "",
  });

  React.useEffect(() => {
    let alive = true;

    async function resolveAccess() {
      const userId = String(auth?.user?.id || "").trim();
      const email = normalizeEmail(auth?.user?.email);
      const localProfile = readPharmacyProfile() || {};
      const localAllowed = hasLocalPharmacyAccess(localProfile, {
        email,
        userId,
      });
      const hasStagingDashboardAccess =
        isStagingLikeRuntime() &&
        localProfile?.stagingDashboardAccess === true &&
        localProfile?.stagingOnboardingBypass === true &&
        localAllowed;

      if (!userId) {
        if (alive) setState({ loading: false, allowed: false, redirectToOnboarding: false, actorId: userId });
        return;
      }

      try {
        const hasDirectOwnerProfile = await hasPersistedPharmacyOwnerProfile(userId).catch(() => false);
        if (hasDirectOwnerProfile) {
          if (alive) {
            setState({
              loading: false,
              allowed: true,
              redirectToOnboarding: false,
              actorId: userId,
            });
          }
          return;
        }

        const { rows, hasOwnerProfile, allowed } = await resolvePersistedPharmacyAccess({
          userId,
          email,
        });
        if (!alive) return;

        if (rows.length > 0) {
          const mergedProfile = mergePersistedPharmacyMembers(localProfile, rows);
          savePharmacyProfile(mergedProfile);
        }

        const hasCompletedAccess =
          hasStagingDashboardAccess ||
          (allowLocalFallback && localAllowed) ||
          hasOwnerProfile ||
          allowed;
        if (hasCompletedAccess) {
          setState({
            loading: false,
            allowed: true,
            redirectToOnboarding: false,
            actorId: userId,
          });
          return;
        }

        const shouldResumePharmacyOnboarding =
          ((await hasPharmacyOnboardingStarted(userId)) || (await isPharmacyInvitee(email)));

        setState({
          loading: false,
          allowed: false,
          redirectToOnboarding: shouldResumePharmacyOnboarding,
          actorId: userId,
        });
      } catch (error) {
        console.warn("[PharmacyProtectedRoute] membership lookup fallback:", error?.message || error);
        if (!alive) return;
        const shouldResumePharmacyOnboarding =
          !allowLocalFallback && ((await hasPharmacyOnboardingStarted(userId)) || (await isPharmacyInvitee(email)));
        setState({
          loading: false,
          allowed: hasStagingDashboardAccess || (allowLocalFallback ? localAllowed : false),
          redirectToOnboarding: shouldResumePharmacyOnboarding,
          actorId: userId,
        });
      }
    }

    resolveAccess();
    return () => {
      alive = false;
    };
  }, [allowLocalFallback, auth?.user?.email, auth?.user?.id]);

  React.useEffect(() => {
    if (!state.allowed || !auth?.user?.id) return;
    writeLastRole("pharmacy", auth.user.id);
  }, [state.allowed, auth?.user?.id]);

  const locallyKnownIncompletePharmacyOnboarding = React.useMemo(() => {
    const userId = String(auth?.user?.id || "").trim();
    if (!userId || auth?.profile?.onboarding_completed === true) return false;

    try {
      const lastRole = readLastRole(userId);
      const lastPath = readLastAuthedPath(userId);
      return lastRole === "pharmacy" && lastPath.startsWith(PHARMACY_ONBOARDING_PATH);
    } catch {
      return false;
    }
  }, [auth?.profile?.onboarding_completed, auth?.user?.id]);

  if (canBypassPharmacyAccessOnLocalhost() && auth?.user?.id) {
    return children;
  }

  if (!state.allowed && locallyKnownIncompletePharmacyOnboarding) {
    return <Navigate to={PHARMACY_ONBOARDING_PATH} replace state={{ from: location }} />;
  }

  if (state.loading || state.actorId !== String(auth?.user?.id || "").trim()) {
    return <RouteFallback label="Opening Pharmacy..." />;
  }

  if (!state.allowed && state.redirectToOnboarding) {
    return <Navigate to={PHARMACY_ONBOARDING_PATH} replace state={{ from: location }} />;
  }

  if (!state.allowed) {
    const params = new URLSearchParams();
    params.set("blocked", "1");
    const email = normalizeEmail(auth?.user?.email);
    if (email) params.set("email", email);
    return <Navigate to={`/pharmacy?${params.toString()}`} replace state={{ from: location }} />;
  }

  return children;
}

export default function PharmacyProtectedRoute({ children }) {
  const location = useLocation();
  const isHelp = /^\/pharmacy\/help(?:\/|$)/.test(location.pathname);
  const loginPath = isHelp ? `/pharmacy?next=${encodeURIComponent(location.pathname + location.search)}` : '/login';
  return (
    <ProtectedRoute loginPath={loginPath}>
      <PharmacyGate>{children}</PharmacyGate>
    </ProtectedRoute>
  );
}
